<script setup>
import { computed } from 'vue';

import icon01 from '@/assets/images/result/ai/icon_01.svg';
import icon02 from '@/assets/images/result/ai/icon_02.svg';
import icon03 from '@/assets/images/result/ai/icon_03.svg';
import icon04 from '@/assets/images/result/ai/icon_04.svg';

const icons = [icon01, icon02, icon03, icon04];

const props = defineProps({
  userRole: { type: String, default: 'student' },
  data: { type: Object, default: () => ({ student: [] }) }
});

const suggestionData = computed(() => {
  // props.data에서 실제 데이터를 가져오고, 없으면 빈 배열 반환
  return props.data?.student || [];
});


</script>

<template>
  <div class="card">
    <h5 class="card-title">AI·디지털 리터러시 향상을 위한 맞춤 학습 제안</h5>
    <div class="ai-table primary mt-4">
      <div class="row thead">
        <div class="th">영역</div>
        <div class="td">학습 방법 및 학습자료</div>
      </div>
      
      <template v-if="suggestionData && suggestionData.length > 0">
        <div class="row" v-for="(item, index) in suggestionData" :key="index">
          <div class="th">
            <!-- 영역 아이콘(icon_01~04 = 기술·활용·윤리·인간중심)은 iconIndex(영역 번호)로 고른다.
                 행 순서는 자료 order 순이라 영역 순서와 다를 수 있다. iconIndex가 없을 때만 행 번호를 쓴다. -->
            <img v-if="icons[item.iconIndex ?? index]" :src="icons[item.iconIndex ?? index]" alt="icon">
            <p v-html="item.category"></p>
          </div>
          <div class="td">
            <div class="td-content">
              <span class="red">한걸음 더</span>
              <div v-if="item.desc" class="description-text">
                {{ item.desc }}
              </div>
              <div v-if="item.resource" class="learning-resources">
                <span>다음 자료로 공부하면 좋아요.</span>
                <div class="link-area">
                  <p>{{ item.resource.text }}</p>
                </div>                
              </div>
            </div>
            <div class="qr-area">
              <div class="qr-placeholder">
                <!-- PDF 호환 텍스트 -->
                <span class="qr-label">추천 학습자료</span>
                <div class="qr-img">
                  <img v-if="item.resource.qr" :src="item.resource.qr" alt="QR코드">
                </div>
              </div>
              <a v-if="item.resource && item.resource.url" :href="item.resource.url" target="_blank" class="go-link">
                바로가기 <span class="arrow">&gt;</span>
              </a>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.report-inner .ai-table .row:not(.thead) > div {  
  &.th {
     flex-direction: column;
     p { font-size: 1.125rem; font-weight: 600; color: var(--result-ai-primary-800); text-align: center; }
  }
  &.td { 
    display: flex;
    padding : 1rem;
    .td-content {
      display: flex;
      flex-direction: column;
      gap: .5rem;
      padding-right: 1rem;
      width: calc(100% - 162px);    
      span {
        font-size: 1rem;
        color: #247CFF;
        background-color: #CCDBF6;
        border-radius: 2rem;       
        font-weight: 600;
        padding: 0 0.75rem;
        height: 30px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        align-self: flex-start;
        white-space: nowrap;
        
        &.red {
          color: #E469A7;
          background-color: #FAE1ED;
        }
      }
      
      .description-text, p {
        white-space: normal;
        // 어절 단위(keep-all)로 두면 오른쪽 여백이 들쭉날쭉해 글자 단위로 끊는다
        word-break: break-all;
        text-align: justify;
      }
      .learning-resources {
        padding-top: .5rem;
        border-top: 1px solid var(--result-ai-primary-100);
        .link-area {
          margin-top: .5rem;
          a { 
            color: var(--result-ai-link); 
            text-decoration: underline;
            text-underline-offset: 3px;
          }
        }
        
      }
    }    
    .qr-area {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      width: 162px;
      height: auto;
      
      .qr-placeholder {
        position: relative;
        width: 162px;
        height: 232px;
        background-image: url("@/assets/images/result/ai/qr_frame.svg");        
        .qr-img {
          position: absolute;
          left: 50%;
          bottom: 2.375rem;
          transform: translateX(-50%);
          width: 110px;
          height: 110px;
          background-color: var(--smoe-white);
          border-radius: .5rem;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        // PDF 호환 레이블 스타일
        .qr-label {
          position: absolute;
          top: 20px;
          right: 6px;
          font-size: 1rem;
          color: var(--result-ai-primary-800);
          font-weight: 600;
        }
        
        // 기존 가상 요소 숨김
        &::after {
          display: none !important;
        }
      }
      // 2026: QR 아래 URL 텍스트는 빼고 '바로가기' 버튼만 남긴다
    }    
   }
}


  /* 2026: 추천 학습자료 바로가기 버튼 (URL 텍스트 대신 이것만 노출) */
  .go-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 0.75rem;
    padding: 3px 12px;
    border: 1px solid var(--result-ai-primary-800, #566391);
    border-radius: 6px;
    background: #fff;
    color: var(--result-ai-primary-800, #566391);
    font-size: 12px;
    font-weight: 600;
    line-height: 1.6;
    text-decoration: none;
    white-space: nowrap;
    .arrow { font-size: 11px; }
  }
</style>
